import { createPortal } from "react-dom";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { ChevronLeftIcon, ChevronDownIcon, XMarkIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { getZendeskHelpCenterArticles, createZendeskTicket } from "../../redux/modules/user/actions";

const ZENDESK_GUIDED_ENTRY_OPEN_EVENT = "zendesk-guided-entry-open";
const ZENDESK_GUIDED_ENTRY_CLOSE_EVENT = "zendesk-guided-entry-close";
const ZENDESK_GUIDED_ENTRY_STATE_EVENT = "zendesk-guided-entry-state";

const SCREENS = {
  ENTRY: "entry",
  FAQ_CATEGORIES: "faq-categories",
  FAQ_LIST: "faq-list",
  CHAT_CONFIG: "chat-config",
  ZENDESK_LOADING: "zendesk-loading",
  ISSUE_FORM: "issue-form",
  ISSUE_SUCCESS: "issue-success",
};

const ENTRY_OPTIONS = {
  FAQ: "faq",
  CHAT: "chat",
  REPORT: "report",
};

const ISSUE_TYPE_OPTIONS = [
  "Login Access",
  "Password/Username",
  "Patient Chart",
  "Medication Review",
  "Minor Ailments",
  "Interventions",
  "Documentation",
  "Rx Vigilance",
];

const URGENCY_OPTIONS = ["Affecting Business Operations", "Not Affecting Business Operations"];

const URGENCY_TOOLTIPS = {
  "Affecting Business Operations": "Unable to continue workflow, clinical services affected, system issue impacting service",
  "Not Affecting Business Operations": "General issue, bug, suggestion, or non-urgent concern, suggestions",
};

const USER_ROLE_OPTIONS = ["Pharmacist", "Admin", "Pilot User"];
const ZENDESK_FETCH_BLOCKED_ROUTES = [
  "/login",
  "/forgot-password",
  "/forgot-username",
  "/forgotten-user-id",
  "/show-username",
  "/check-email",
  "/confirmed-new-link",
  "/confirmed-new-invite",
  "/change-password",
  "/mfa-verification",
  "/password-changed-success",
  "/portal/selection",
];

const getFocusableElement = (container) => {
  if (!container) {
    return null;
  }

  return container.querySelector(
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
};

/** Transform raw Zendesk API data into categories and FAQs (articles mapped to categories via sections). */
const transformZendeskData = (data) => {
  if (!data?.categories?.length && !data?.sections?.length && !data?.articles?.length) {
    return { categories: [], faqs: [] };
  }
  const categoriesData = data.categories || [];
  const sectionsData = data.sections || [];
  const articlesData = data.articles || [];

  const sectionToCategoryMap = {};
  sectionsData.forEach((section) => {
    sectionToCategoryMap[String(section.id)] = String(section.category_id);
  });

  const categories = categoriesData.map((category) => ({
    id: String(category.id),
    title: category.name,
  }))
    .sort((a, b) => a.title.localeCompare(b.title));


  const faqs = articlesData.map((article) => {
    let description = '';
    if (article.body) {
      description = article.body.replace(/<[^>]*>/g, '');
      if (typeof document !== 'undefined') {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = description;
        description = tempDiv.textContent || tempDiv.innerText || '';
      }
      description = description.replace(/\s+/g, ' ').trim();
    }
    const sectionId = String(article.section_id);
    const categoryId = sectionToCategoryMap[sectionId] || sectionId;
    return {
      id: String(article.id),
      categoryId,
      title: article.title,
      description,
    };
  });

  return { categories, faqs };
};

/**
 * Convert a display label to the Zendesk tag/field format.
 * e.g. "Login Access" → "login_access", "Password/Username" → "password_username"
 */
const toZendeskValue = (str) =>
  str?.toLowerCase().replace(/\s+/g, "_") ?? "";

/**
 * Convert dropdown values to Zendesk tags.
 * e.g. issueType "Login Access" → "issue_login_access"
 */
const generateZendeskTags = (dropdownValues) => {
  const tags = [];
  if (dropdownValues.issueType) {
    tags.push(`issue_${toZendeskValue(dropdownValues.issueType)}`);
  }
  if (dropdownValues.urgency) {
    tags.push(`urgency_${toZendeskValue(dropdownValues.urgency)}`);
  }
  if (dropdownValues.userRole) {
    tags.push(`role_${toZendeskValue(dropdownValues.userRole)}`);
  }
  return tags;
};

/**
 * Build conversation fields for Zendesk messenger:set.
 * Values are converted to tag format (lowercase_underscore) to match
 * the field option tags set in Zendesk Admin.
 * Priority is intentionally excluded — Zendesk triggers handle it automatically.
 */
const buildConversationFields = (dropdownValues) => {
  const fields = [];
  const idIssue = import.meta.env?.VITE_ZENDESK_FIELD_ID_ISSUE_TYPE;
  const idUrgency = import.meta.env?.VITE_ZENDESK_FIELD_ID_URGENCY;
  const idRole = import.meta.env?.VITE_ZENDESK_FIELD_ID_USER_ROLE;

  if (idIssue && dropdownValues.issueType)
    fields.push({ id: Number(idIssue), value: toZendeskValue(dropdownValues.issueType) });
  if (idUrgency && dropdownValues.urgency)
    fields.push({ id: Number(idUrgency), value: toZendeskValue(dropdownValues.urgency) });
  if (idRole && dropdownValues.userRole)
    fields.push({ id: Number(idRole), value: toZendeskValue(dropdownValues.userRole) });

  return fields;
};

const ZendeskGuidedEntry = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const zendeskHelpCenterLoading = useSelector((state) => state.users?.zendeskHelpCenterLoading ?? false);
  const zendeskHelpCenterData = useSelector((state) => state.users?.zendeskHelpCenterData ?? null);
  const zendeskHelpCenterFetchAttempted = useSelector(
    (state) => state.users?.zendeskHelpCenterFetchAttempted ?? false
  );
  const [open, setOpen] = useState(false);
  const [currentScreen, setCurrentScreen] = useState(SCREENS.ENTRY);
  const [, setSelectedEntryOption] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedFAQId, setExpandedFAQId] = useState(null);
  const [showAllFaqs, setShowAllFaqs] = useState(false);
  const [, setNavigationStack] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownValues, setDropdownValues] = useState({
    issueType: null,
    urgency: null,
    userRole: "Pilot User",
  });
  const [faqsToShowCount, setFaqsToShowCount] = useState(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isMeasured, setIsMeasured] = useState(false);
  const [flowType, setFlowType] = useState(null);
  const [urgencyTooltip, setUrgencyTooltip] = useState(null);
  const [attachmentTooltip, setAttachmentTooltip] = useState(null);
  const [issueDescription, setIssueDescription] = useState("");
  const [issueAttachment, setIssueAttachment] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const { categories, faqs } = useMemo(
    () => transformZendeskData(zendeskHelpCenterData),
    [zendeskHelpCenterData]
  );

  const modalRef = useRef(null);
  const contentRef = useRef(null);
  const faqContainerRef = useRef(null);
  const faqListRef = useRef(null);

  const canContinueFromChatConfig =
    Boolean(dropdownValues.issueType) &&
    Boolean(dropdownValues.urgency) &&
    Boolean(dropdownValues.userRole);

  const filteredFaqs = useMemo(() => {
    if (!selectedCategory) {
      return [];
    }

    return faqs.filter((faq) => faq.categoryId === selectedCategory.id);
  }, [selectedCategory, faqs]);

  const normalizedPathname = useMemo(() => location.pathname.replace(/\/$/, ""), [location.pathname]);
  const shouldFetchZendeskHelpCenterArticles = useMemo(() => {
    const accessToken = sessionStorage.getItem("accessToken");
    // Fetch only after login and never on auth/portal selection screens.
    return Boolean(accessToken) && !ZENDESK_FETCH_BLOCKED_ROUTES.includes(normalizedPathname);
  }, [normalizedPathname]);

  const resetFlow = useCallback(() => {
    setCurrentScreen(SCREENS.ENTRY);
    setSelectedEntryOption(null);
    setSelectedCategory(null);
    setExpandedFAQId(null);
    setShowAllFaqs(false);
    setNavigationStack([]);
    setOpenDropdown(null);
    setDropdownValues({
      issueType: null,
      urgency: null,
      userRole: "Pilot User",
    });
    setFlowType(null);
    setUrgencyTooltip(null);
    setAttachmentTooltip(null);
    setIssueDescription("");
    setIssueAttachment([]);
    setIsSubmitting(false);
    setSubmitError(null);
  }, []);

  const closeFlow = useCallback(() => {
    setOpen(false);
    setOpenDropdown(null);
  }, []);

  const goTo = (screen) => {
    setNavigationStack((prev) => [...prev, currentScreen]);
    setCurrentScreen(screen);
    setExpandedFAQId(null);
    setOpenDropdown(null);
    if (screen === SCREENS.FAQ_LIST) {
      setShowAllFaqs(false);
      setFaqsToShowCount(null);
      setHasOverflow(false);
      setIsMeasured(false);
    }
  };

  const goBack = () => {
    setNavigationStack((prev) => {
      if (!prev.length) {
        setCurrentScreen(SCREENS.ENTRY);
        setSelectedEntryOption(null);
        setSelectedCategory(null);
        return prev;
      }

      const nextStack = [...prev];
      const previousScreen = nextStack.pop();
      setCurrentScreen(previousScreen || SCREENS.ENTRY);
      if (!previousScreen || previousScreen === SCREENS.ENTRY) {
        setSelectedEntryOption(null);
        setSelectedCategory(null);
      }
      setExpandedFAQId(null);
      setOpenDropdown(null);
      return nextStack;
    });
  };

  const openZendeskChat = () => {
    if (typeof window !== "undefined" && typeof window.__HC_OPEN_ZENDESK_CHAT === "function") {
      window.__HC_OPEN_ZENDESK_CHAT();
      return;
    }

    if (typeof window !== "undefined" && typeof window.zE === "function") {
      window.zE("messenger", "open");
    }
  };

  const handleChatSave = () => {
    if (!canContinueFromChatConfig) {
      return;
    }

    // Set conversation tags (for trigger-based routing)
    const tags = generateZendeskTags(dropdownValues);
    if (tags.length > 0 && typeof window.zE === "function") {
      try {
        window.zE("messenger:set", "conversationTags", tags);
      } catch (err) {
        console.warn("Could not set Zendesk tags:", err);
      }
    }

    // Set conversation fields (sends dropdown values to Zendesk ticket fields)
    const conversationFields = buildConversationFields(dropdownValues);
    if (conversationFields.length > 0 && typeof window.zE === "function") {
      try {
        window.zE("messenger:set", "conversationFields", conversationFields);
      } catch (err) {
        console.warn("Zendesk conversation fields could not be set:", err);
      }
    }

    setCurrentScreen(SCREENS.ZENDESK_LOADING);
    openZendeskChat();

    setTimeout(() => {
      closeFlow();
    }, 500);
  };

  const handleSubmitIssue = () => {
    if (!issueDescription.trim() || isSubmitting) return;

    // Navigate to success immediately so the user sees confirmation right away.
    // Ticket creation runs in the background.
    goTo(SCREENS.ISSUE_SUCCESS);

    dispatch(
      createZendeskTicket({
        dropdownValues,
        description: issueDescription.trim(),
        attachments: issueAttachment,
      })
    );
  };

  const selectCategory = (category) => {
    setSelectedCategory(category);
    setShowAllFaqs(false);
    setFaqsToShowCount(null);
    setHasOverflow(false);
    goTo(SCREENS.FAQ_LIST);
  };

  const toggleFaq = (faqId) => {
    setExpandedFAQId((prev) => (prev === faqId ? null : faqId));
  };

  const selectDropdownValue = (field, value) => {
    setDropdownValues((prev) => ({ ...prev, [field]: value }));
    setOpenDropdown(null);
  };

  useEffect(() => {
    if (
      !shouldFetchZendeskHelpCenterArticles ||
      zendeskHelpCenterData ||
      zendeskHelpCenterLoading ||
      zendeskHelpCenterFetchAttempted
    ) {
      return;
    }
    dispatch(getZendeskHelpCenterArticles());
  }, [
    dispatch,
    shouldFetchZendeskHelpCenterArticles,
    zendeskHelpCenterData,
    zendeskHelpCenterLoading,
    zendeskHelpCenterFetchAttempted,
  ]);


  useEffect(() => {
    const handleOpenFlow = (event) => {
      const shouldResumeChatFlow = Boolean(event?.detail?.resumeChatFlow);

      if (shouldResumeChatFlow) {
        setSelectedEntryOption(ENTRY_OPTIONS.CHAT);
        setCurrentScreen(SCREENS.CHAT_CONFIG);
        setNavigationStack([SCREENS.ENTRY]);
        setSelectedCategory(null);
        setExpandedFAQId(null);
        setOpenDropdown(null);
        setOpen(true);
        return;
      }

      setOpen(true);
    };
    const handleCloseFlow = () => {
      closeFlow();
    };

    window.addEventListener(ZENDESK_GUIDED_ENTRY_OPEN_EVENT, handleOpenFlow);
    window.addEventListener(ZENDESK_GUIDED_ENTRY_CLOSE_EVENT, handleCloseFlow);

    return () => {
      window.removeEventListener(ZENDESK_GUIDED_ENTRY_OPEN_EVENT, handleOpenFlow);
      window.removeEventListener(ZENDESK_GUIDED_ENTRY_CLOSE_EVENT, handleCloseFlow);
    };
  }, [closeFlow, resetFlow]);

  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
      window.dispatchEvent(
        new CustomEvent(ZENDESK_GUIDED_ENTRY_STATE_EVENT, {
          detail: { open },
        })
      );
    }
  }, [open]);

  useEffect(() => {
    if (!open || !openDropdown) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      const clickedInsideDropdown = event.target?.closest?.("[data-zendesk-dropdown='true']");
      if (!clickedInsideDropdown) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [closeFlow, open, openDropdown]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        if (openDropdown) {
          setOpenDropdown(null);
          return;
        }

        closeFlow();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [closeFlow, open, openDropdown]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const id = window.requestAnimationFrame(() => {
      const nextFocusable = getFocusableElement(contentRef.current);
      if (nextFocusable) {
        nextFocusable.focus();
      }
    });

    return () => {
      window.cancelAnimationFrame(id);
    };
  }, [open, currentScreen]);

  // Detect overflow and determine how many FAQs fit
  useLayoutEffect(() => {
    if (currentScreen !== SCREENS.FAQ_LIST || !filteredFaqs.length || showAllFaqs) {
      setHasOverflow(false);
      setFaqsToShowCount(null);
      setIsMeasured(true);
      return;
    }

    const SELECT_ALL_BUTTON_HEIGHT = 52;

    const measure = () => {
      if (!faqContainerRef.current || !faqListRef.current) {
        setIsMeasured(true);
        return;
      }

      const container = faqContainerRef.current;
      const list = faqListRef.current;
      
      // Get all FAQ items
      const allFaqItems = Array.from(list.querySelectorAll('[data-faq-item]'));
      if (allFaqItems.length === 0) {
        setIsMeasured(true);
        return;
      }

      const containerHeight = container.clientHeight;
      const availableHeight = containerHeight - SELECT_ALL_BUTTON_HEIGHT;

      // Temporarily show all hidden items to measure them
      const originalDisplays = allFaqItems.map(item => {
        const wasHidden = item.classList.contains('hidden');
        if (wasHidden) item.classList.remove('hidden');
        return wasHidden;
      });

      // Force reflow to get accurate measurements
      void container.offsetHeight;

      // Measure each item and calculate how many fit
      let totalHeight = 0;
      let fittingCount = 0;
      for (let i = 0; i < allFaqItems.length; i++) {
        const itemHeight = allFaqItems[i].offsetHeight;
        if (totalHeight + itemHeight <= availableHeight) {
          totalHeight += itemHeight;
          fittingCount++;
        } else {
          break;
        }
      }

      // Restore original display state
      allFaqItems.forEach((item, index) => {
        if (originalDisplays[index]) item.classList.add('hidden');
      });

      // Determine if we have overflow
      if (fittingCount > 0 && fittingCount < filteredFaqs.length) {
        setHasOverflow(true);
        setFaqsToShowCount(fittingCount);
      } else {
        setHasOverflow(false);
        setFaqsToShowCount(null);
      }
      setIsMeasured(true);
    };

    const rafId = requestAnimationFrame(measure);

    const handleResize = () => {
      setIsMeasured(false);
      requestAnimationFrame(measure);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
    };
  }, [currentScreen, filteredFaqs, showAllFaqs]);

  if (!open || typeof document === "undefined") {
    return null;
  }

  const renderHeaderTitle = () => {
    if (currentScreen === SCREENS.FAQ_CATEGORIES) {
      return "REEL Support";
    }

    return "REEL Support";
  };

  const renderEntryScreen = () => {
    const optionBaseClass =
      "w-[350px] h-[56px] border rounded-[4px] py-2 pl-[60px] pr-3 font-semibold transition-colors duration-200 flex items-center justify-start gap-[10px]";
    const inactiveClass = "border-[#006b86] text-white bg-[#0a85a8] hover:bg-[#0b7b9a]";
    const greyClass = "border-gray-300 text-gray-600 bg-gray-20 cursor-not-allowed";

    return (
      <div className="pt-[10px] mt-[30px] px-10">
        <div className="flex flex-col items-center gap-5">
          <button
            type="button"
            className={`${optionBaseClass} ${inactiveClass}`}
            onClick={() => {
              setSelectedEntryOption(ENTRY_OPTIONS.FAQ);
              goTo(SCREENS.FAQ_CATEGORIES);
            }}
            aria-pressed={false}
          >
            <img src="/icons/FAQs.svg" alt="" className="w-[24px] h-[24px]" />
            <span className="text-[20px] leading-none font-medium">FAQs / Help Centre</span>
          </button>

          <button
            type="button"
            className={`${optionBaseClass} ${inactiveClass}`}
            onClick={() => {
              setFlowType("report");
              setSelectedEntryOption(ENTRY_OPTIONS.REPORT);
              goTo(SCREENS.CHAT_CONFIG);
            }}
          >
            <img src="/icons/report-an-issue.svg" alt="" className="w-[24px] h-[24px] shrink-0" />
            <span className="text-[20px] leading-none font-medium">Report an Issue</span>
          </button>

          <div className="relative group">
            <button
              type="button"
              className={`${optionBaseClass} ${greyClass}`}
              disabled
              aria-disabled="true"
            >
              <img src="/icons/chat-with-us-grey.svg" alt="" className="w-[24px] h-[24px]" />
              <span className="text-[20px] leading-none font-medium">Chat With Us</span>
            </button>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 text-white text-[13px] font-medium rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-10">
              Coming Soon
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFaqCategories = () => {
    if (zendeskHelpCenterLoading) {
      return (
        <div className="px-10 mx-auto mt-[30px] pt-[10px] h-full flex flex-col min-h-0 items-center justify-center">
          <div className="w-[40px] h-[40px] border-[4px] border-[#C7EAFB] border-t-teal-300 rounded-full animate-spin"></div>
        </div>
      );
    }

    return (
      <div className="px-10 mx-auto mt-[30px] pt-[10px] h-full flex flex-col min-h-0">
        <h2 className="text-[22px] leading-tight font-medium text-teal-300 text-center mb-5">
          How can we help?
        </h2>
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pr-1">
          <div className="space-y-4 pb-1 pt-[10px] flex flex-col items-center overflow-x-hidden">
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                className="w-[350px] text-left px-3 py-2 h-[56px] border border-teal-300 rounded-[4px] text-teal-300 text-[18px] font-medium hover:bg-[#eaf4f7] transition-colors break-words"
                onClick={() => selectCategory(category)}
              >
                {category.title}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderFaqList = () => {
    if (zendeskHelpCenterLoading) {
      return (
        <div className="px-10 pt-[20px] h-full flex flex-col min-h-0 items-center justify-center">
          <div className="w-[40px] h-[40px] border-[4px] border-[#C7EAFB] border-t-teal-300 rounded-full animate-spin"></div>
        </div>
      );
    }

    const shouldShowSelectAllButton = hasOverflow && !showAllFaqs;
    const isTruncated = shouldShowSelectAllButton && faqsToShowCount !== null && faqsToShowCount > 0;
    const scrollContainerClass =
      showAllFaqs || !hasOverflow || (shouldShowSelectAllButton && expandedFAQId !== null)
        ? "flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
        : "flex-1 min-h-0 overflow-hidden overflow-x-hidden";

    return (
      <div className={`px-10 pt-[20px] h-full flex flex-col min-h-0 overflow-hidden${isMeasured ? '' : ' invisible'}`}>
        <h2 className="text-[22px] leading-tight font-semibold text-teal-300 mb-[10px] shrink-0 break-words">
          {selectedCategory?.title || "FAQs"}
        </h2>
        <div className="h-px bg-gray-300 mb-[14px] shrink-0" />

        <div ref={faqContainerRef} className={scrollContainerClass}>
          <div ref={faqListRef} className="overflow-x-hidden pb-1 pr-1">
            {/* Always render all FAQs for measurement, but conditionally display */}
            {filteredFaqs.map((faq, index) => {
              const isExpanded = expandedFAQId === faq.id;
              const shouldDisplay = !isTruncated || index < faqsToShowCount;

              return (
                <div 
                  key={faq.id} 
                  data-faq-item 
                  className={`border-b w-[470px] border-gray-300 shrink-0 overflow-x-hidden ${shouldDisplay ? '' : 'hidden'}`}
                >
                  <button
                    type="button"
                    className="min-h-[52px] w-full pl-[10px] pr-[16px] flex items-center bg-gray-10 rounded-[4px] text-[18px] text-semibold text-gray-800 justify-between gap-4 text-left py-2"
                    onClick={() => toggleFaq(faq.id)}
                    aria-expanded={isExpanded}
                  >
                    <span className="text-[18px] leading-snug font-semibold text-gray-800 flex-1 min-w-0 break-words">
                      • {faq.title}
                    </span>
                    <span className="w-[20px] h-[20px] text-[20px] font-light leading-none text-[#565656] shrink-0 self-center flex items-center justify-center" aria-hidden="true">
                      {isExpanded ? "−" : "+"}
                    </span>
                  </button>

                  {isExpanded && (
                    <div className="px-[20px] bg-gray-10 py-[10px] shrink-0 overflow-x-hidden">
                      <p className="text-[16px] text-gray-800 font-normal leading-relaxed break-words">
                        {faq.description}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}

            {shouldShowSelectAllButton && (
              <div className="border-gray-300 shrink-0 overflow-x-hidden">
                <button
                  type="button"
                  className="min-h-[52px] w-full pl-[10px] pr-[16px] flex items-center rounded-[4px] text-left py-2"
                  onClick={() => setShowAllFaqs(true)}
                >
                  <span className="text-[18px] font-semibold text-gray-800 break-words">
                    • Select All FAQs
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderDropdown = (field, label, options, tooltips = null) => {
    const selectedValue = dropdownValues[field];
    const isOpen = openDropdown === field;
    const dropdownListMaxHeightClass =
      field === "issueType" ? "max-h-[420px]" : "max-h-[240px]";

    return (
      <div className="relative" data-zendesk-dropdown="true">
        <button
          type="button"
          className={`w-full h-[56px] border border-[#0a6d88] rounded-[4px] py-2 px-3 text-[16px] font-medium flex items-center justify-between text-teal-300 ${selectedValue ? "bg-gray-20" : "bg-white"
            }`}
          onClick={() => setOpenDropdown((prev) => (prev === field ? null : field))}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <div className="flex items-center min-w-0 flex-1">
            <span className="text-[16px] leading-none font-medium truncate">{selectedValue || label}</span>
            {tooltips && selectedValue && tooltips[selectedValue] && (
              <span
                className="ml-[10px] shrink-0 flex items-center justify-center"
                onMouseEnter={(e) => {
                  e.stopPropagation();
                  const rect = e.currentTarget.getBoundingClientRect();
                  setUrgencyTooltip({ option: selectedValue, rect });
                }}
                onMouseLeave={() => setUrgencyTooltip(null)}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Info: ${selectedValue}`}
              >
                <img src="/icons/info-new.svg" alt="" className="w-5 h-5 shrink-0" />
              </span>
            )}
          </div>
          <ChevronDownIcon className="w-[20px] h-[20px] stroke-[2.5] shrink-0" />
        </button>

        {isOpen && (
          <div
            className={`absolute left-0 right-0 mt-1 border border-gray-200 bg-white shadow-lg z-20 overflow-y-auto ${dropdownListMaxHeightClass}`}
          >
            {options.map((option) => {
              const isSelected = selectedValue === option;
              const hasTooltip = Boolean(tooltips?.[option]);
              return (
                <button
                  key={option}
                  type="button"
                  className={`w-full flex items-center h-[40px] px-[10px] py-[10px] text-[14px] font-normal border-b border-gray-100 last:border-b-0 ${isSelected ? "bg-gray-20 text-teal-300 font-semibold" : "bg-white text-[#525252] hover:bg-teal-25"
                    }`}
                  onClick={() => selectDropdownValue(field, option)}
                  role="option"
                  aria-selected={isSelected}
                >
                  <div className="flex items-center">
                    <span className="text-left">{option}</span>
                    {hasTooltip && (
                      <span
                        className="ml-[10px] shrink-0 flex items-center justify-center"
                        onMouseEnter={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          setUrgencyTooltip({ option, rect });
                        }}
                        onMouseLeave={() => setUrgencyTooltip(null)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Info: ${option}`}
                      >
                        <img src="/icons/info-new.svg" alt="" className="w-5 h-5 shrink-0" />
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderChatConfig = () => {
    return (
      <div className="px-10 pt-[10px] mt-[30px] h-full flex flex-col min-h-0">
        <div className="space-y-4 w-full max-w-[350px] mx-auto">
          {renderDropdown("issueType", "Select Issue Type", ISSUE_TYPE_OPTIONS)}
          {renderDropdown("urgency", "Select Urgency Level", URGENCY_OPTIONS, URGENCY_TOOLTIPS)}
          {renderDropdown("userRole", "Select User Role", USER_ROLE_OPTIONS)}
        </div>
      </div>
    );
  };

  const renderLoadingScreen = () => {
    return (
      <div className="flex h-full flex-col items-center justify-center pb-20 gap-4">
        <div className="w-[40px] h-[40px] border-[4px] border-[#C7EAFB] border-t-teal-300 rounded-full animate-spin"></div>
        <p className="text-teal-300 font-medium text-[16px]">Opening chat...</p>
      </div>
    );
  };

  const renderIssueForm = () => {
    return (
      <div className="px-10 pt-[10px] mt-[10px] flex flex-col gap-5 w-full overflow-y-auto h-full">
        <div className="bg-yellow-60 rounded-[4px] p-5 shrink-0">
          <p className="text-teal-600 text-[18px] font-semibold leading-[30px]">
            Do not include patient names, date of birth, health card numbers, or other personal health information in tickets or screenshots.
          </p>
        </div>

        <div className="flex flex-col gap-[5px] shrink-0">
          <label
            htmlFor="zendesk-issue-description"
            className="text-gray-800 text-[16px] font-medium leading-[24px]"
          >
            Describe the Issue Here
          </label>
          <textarea
            id="zendesk-issue-description"
            className="w-full h-[200px] border-[1.5px] border-[#017E9D] rounded-[10px] px-5 py-[10px] text-[16px] text-[#424242] resize-none outline-none ring-0 focus:outline-none focus:ring-0 focus:border-[#017E9D]"
            value={issueDescription}
            onChange={(e) => setIssueDescription(e.target.value)}
            maxLength={5000}
          />
        </div>

        <div className="flex flex-col gap-[10px] shrink-0">
          <div className="flex items-center gap-[10px]">
            <span className="text-gray-800 text-[16px] font-medium leading-[24px]">
              Add an Attachment (Optional)
            </span>
            <span
              className="shrink-0 cursor-default"
              onMouseEnter={(e) => setAttachmentTooltip(e.currentTarget.getBoundingClientRect())}
              onMouseLeave={() => setAttachmentTooltip(null)}
            >
              <img src="/icons/info-new.svg" alt="" className="w-5 h-5" />
            </span>
          </div>

          {issueAttachment.length > 0 && (
            <div className="flex flex-col gap-[6px]">
              {issueAttachment.map((file, idx) => (
                <div key={idx} className="flex items-center gap-[10px]">
                  <img src="/icons/attach_file_new.svg" alt="attachment" className="w-[15px] h-[20px] shrink-0" />
                  <span className="text-gray-800 text-[14px] font-normal leading-[22px] break-all flex-1">
                    {file.name}
                  </span>
                  <button
                    type="button"
                    className="text-[12px] text-red-500 shrink-0 hover:underline"
                    onClick={() => setIssueAttachment((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          <label className="w-fit h-[44px] border-[1.5px] border-[#017E9D] rounded-[10px] px-[20px] py-[10px] flex items-center justify-center cursor-pointer hover:bg-[#f0fafb] transition-colors">
            <span className="text-gray-800 text-[16px] font-normal leading-[24px] whitespace-nowrap">
              Upload from Device
            </span>
            <input
              type="file"
              multiple
              className="sr-only"
              onChange={(e) => {
                const newFiles = Array.from(e.target.files ?? []);
                setIssueAttachment((prev) => [...prev, ...newFiles]);
                e.target.value = "";
              }}
              accept="image/png,image/jpeg,.png,.jpg,.jpeg"
            />
          </label>
        </div>

        {submitError && (
          <p className="text-red-500 text-[14px] shrink-0">{submitError}</p>
        )}
      </div>
    );
  };

  const renderIssueSuccess = () => {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-10 h-full">
        <div className="bg-green-50 rounded-[4px] p-5 w-full">
          <p className="text-gray-800 text-[16px] font-medium leading-[30px]">
            Thank you — your issue has been submitted to the REEL support team.
          </p>
        </div>
      </div>
    );
  };

  const showBackButton =
    currentScreen !== SCREENS.ENTRY &&
    currentScreen !== SCREENS.ZENDESK_LOADING;

  const shouldShowFooterButton =
    currentScreen === SCREENS.CHAT_CONFIG ||
    currentScreen === SCREENS.ISSUE_FORM ||
    currentScreen === SCREENS.ISSUE_SUCCESS;

  const saveDisabled = (() => {
    if (currentScreen === SCREENS.ISSUE_SUCCESS) return false;
    if (currentScreen === SCREENS.ISSUE_FORM) return !issueDescription.trim() || isSubmitting;
    return !canContinueFromChatConfig;
  })();

  const footerButtonLabel = (() => {
    if (currentScreen === SCREENS.ISSUE_SUCCESS) return "Close";
    if (currentScreen === SCREENS.ISSUE_FORM) return isSubmitting ? "Submitting..." : "Save & Submit";
    return "Save & Continue";
  })();

  const handleFooterButtonClick = () => {
    if (currentScreen === SCREENS.ISSUE_SUCCESS) {
      resetFlow();
      closeFlow();
    } else if (currentScreen === SCREENS.ISSUE_FORM) {
      handleSubmitIssue();
    } else if (flowType === "report") {
      goTo(SCREENS.ISSUE_FORM);
    } else {
      handleChatSave();
    }
  };

  const contentByScreen = () => {
    if (currentScreen === SCREENS.ENTRY) return renderEntryScreen();
    if (currentScreen === SCREENS.FAQ_CATEGORIES) return renderFaqCategories();
    if (currentScreen === SCREENS.FAQ_LIST) return renderFaqList();
    if (currentScreen === SCREENS.ZENDESK_LOADING) return renderLoadingScreen();
    if (currentScreen === SCREENS.ISSUE_FORM) return renderIssueForm();
    if (currentScreen === SCREENS.ISSUE_SUCCESS) return renderIssueSuccess();
    return renderChatConfig();
  };

  const tooltipContent = urgencyTooltip && URGENCY_TOOLTIPS[urgencyTooltip.option];
  const tooltipWidth = 241;
  const iconCenterX = urgencyTooltip
    ? urgencyTooltip.rect.left + urgencyTooltip.rect.width / 2
    : 0;
  const tooltipLeft = urgencyTooltip
    ? Math.max(8, iconCenterX - tooltipWidth + 24)
    : 0;
  const caretLeft = urgencyTooltip
    ? Math.round(iconCenterX - tooltipLeft - 7)
    : 0;

  return createPortal(
    <>
    <div
      ref={modalRef}
      className="fixed right-5 bottom-[76px] z-[2147483644] w-[550px] max-w-[calc(100vw-40px)] h-[682px] max-h-[calc(100vh-76px)] bg-white shadow-[0_10px_40px_rgba(0,0,0,0.25)] flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={renderHeaderTitle()}
    >
      <div className="h-[62px] w-[550px] bg-[#C7EAFB] px-5 flex items-center justify-between shrink-0">
        <div className="flex items-center min-w-0">
          {showBackButton && (
            <button
              type="button"
              className={`${currentScreen === SCREENS.ISSUE_SUCCESS ? "w-[44px] h-[44px]" : "w-[35px] h-[35px]"} rounded-full border border-teal-300 bg-white text-teal-300 flex items-center justify-center hover:bg-teal-10 transition-colors mr-[30px] shrink-0`}
              onClick={goBack}
              aria-label="Go back"
            >
              <ChevronLeftIcon className={`${currentScreen === SCREENS.ISSUE_SUCCESS ? "w-6 h-6" : "w-5 h-5"} stroke-[2.5]`} />
            </button>
          )}

          <div className="w-11 h-11 rounded-full bg-transparent flex items-center justify-center">
            <img src="/logo/Rx-new.svg" alt="" className="w-[42px] h-[42px]" />
          </div>
          <h1 className="text-[22px] leading-none font-semibold text-teal-300 truncate pl-[16px]">REEL Support</h1>
        </div>

        {currentScreen !== SCREENS.ZENDESK_LOADING && (
          <button
            type="button"
            className="w-9 h-9 rounded-full bg-[#bee4f2] text-[#0a5064] hover:bg-[#9dd5ea] flex items-center justify-center"
            onClick={() => { resetFlow(); closeFlow(); }}
            aria-label="Close support flow"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      <div ref={contentRef} className="flex-1 min-h-0 overflow-hidden">
        {contentByScreen()}
      </div>

      <div className=" px-8 pb-6 flex items-end justify-end shrink-0">
        {shouldShowFooterButton && (
          <button
            type="button"
            className={`h-[38px] ${currentScreen === SCREENS.ISSUE_SUCCESS ? "" : "min-w-[185px]"} py-[7px] px-[15px] rounded-full text-[16px] font-semibold flex items-center justify-center gap-2 ${saveDisabled
              ? "bg-gray-50 text-white cursor-not-allowed"
              : "bg-[#0a85a8] text-white hover:bg-[#0b7592]"
              }`}
            disabled={saveDisabled}
            onClick={handleFooterButtonClick}
          >
            {footerButtonLabel}
            <span
              className={`flex items-center justify-center text-[17px] leading-none ${saveDisabled ? "w-[17px] h-[17px] rounded-full border border-gray-100 text-white" : `w-[17px] h-[17px] text-white`
                }`}
              aria-hidden="true"
            >
              ›
            </span>
          </button>
        )}
      </div>
    </div>

    {tooltipContent && (
      <div
        className="fixed z-[2147483645] pointer-events-none"
        style={{ left: tooltipLeft, top: urgencyTooltip.rect.bottom + 6 }}
      >
        <div
          style={{
            marginLeft: caretLeft,
            width: 0,
            height: 0,
            borderLeft: "7px solid transparent",
            borderRight: "7px solid transparent",
            borderBottom: "8px solid #F5F5F5",
            marginBottom: "-1px",
          }}
        />
        <div
          style={{
            backgroundColor: "#F5F5F5",
            borderRadius: "8px",
            padding: "8px",
            width: `${tooltipWidth}px`,
            boxShadow: "0px 6px 5px rgba(0,0,0,0.25)",
          }}
        >
          <p style={{ fontSize: "14px", color: "#424242", lineHeight: "20px", fontWeight: 400, margin: 0 }}>
            {tooltipContent}
          </p>
        </div>
      </div>
    )}

    {attachmentTooltip && (
      <div
        className="fixed z-[2147483645] pointer-events-none"
        style={{
          left: Math.max(8, attachmentTooltip.left + attachmentTooltip.width / 2 - 217),
          top: attachmentTooltip.bottom + 6,
        }}
      >
        <div
          style={{
            marginLeft: Math.round(
              attachmentTooltip.left + attachmentTooltip.width / 2 -
              Math.max(8, attachmentTooltip.left + attachmentTooltip.width / 2 - 217) - 7
            ),
            width: 0,
            height: 0,
            borderLeft: "7px solid transparent",
            borderRight: "7px solid transparent",
            borderBottom: "8px solid #F5F5F5",
            marginBottom: "-1px",
          }}
        />
        <div
          style={{
            backgroundColor: "#F5F5F5",
            borderRadius: "8px",
            padding: "8px",
            width: "241px",
            boxShadow: "0px 6px 5px rgba(0,0,0,0.25)",
          }}
        >
          <p style={{ fontSize: "14px", color: "#424242", lineHeight: "20px", fontWeight: 400, margin: 0 }}>
            Acceptable types of files: PNG, JPEG
          </p>
        </div>
      </div>
    )}

    </>,
    document.body
  );
};

export default ZendeskGuidedEntry;